import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { 
  DollarSign, 
  Calendar, 
  ArrowDownCircle, 
  ArrowUpCircle,
  FileDown,
  FileSpreadsheet,
  FileCode
} from "lucide-react";
import ResourcePage from "../../components/ResourcePage";
import { api } from "../../axios/api";
import { notify } from "../../utils/toastAlert";
import { downloadResponse } from "../../utils/downloadFile";

const FluxoCaixa = () => {
  const { user, empresa } = useSelector((state) => state.user);
  const [loading, setLoading] = useState(false);
  const [dadosCaixa, setDadosCaixa] = useState(null);

  const [filtros, setFiltros] = useState({
    inicio: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    fim: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0]
  });

  const fetchFluxoCaixa = async () => {
    try {
      setLoading(true);
      const response = await api.post("/caixa/fluxo", {
        empresa_id: empresa?.id || user?.empresa_id,
        inicio: filtros.inicio,
        fim: filtros.fim
      });
      setDadosCaixa(response.data?.data || response.data);
    } catch (err) {
      console.error("Erro ao carregar o fluxo de caixa:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (empresa?.id || user?.empresa_id) {
      fetchFluxoCaixa();
    }
  }, [empresa?.id, user?.empresa_id]);

  const handleDateChange = (e) => {
    setFiltros({ ...filtros, [e.target.name]: e.target.value });
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchFluxoCaixa();
  };

  // Funções de Exportação (Simulação / Integração com endpoints ou download direto)
  // eslint-disable-next-line no-unused-vars
  const exportReport = async (format) => {
    try {
      await notify("A gerar relatório em PDF...", "info");
      // Adicione a chamada real à API se houver endpoint, ex: api.get(...)
    } catch (err) {
      await notify("Erro ao exportar PDF.", "error");
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleExportExcel = async () => {
    try {
      await notify("A gerar folha Excel...", "info");
    } catch (err) {
      await notify("Erro ao exportar Excel.", "error");
    }
  };

  const downloadExport = async (format) => {
    try {
      const response = await api.get(`/caixa/fluxo/export/${format}`, {
        params: {
          empresa_id: empresa?.id || user?.empresa_id,
          inicio: filtros.inicio,
          fim: filtros.fim,
        },
        responseType: "blob",
      });
      downloadResponse(response, `fluxo-caixa-${filtros.inicio}-${filtros.fim}.${format === "excel" ? "xls" : "pdf"}`);
      await notify("Relatorio exportado com sucesso.", "success");
    } catch (err) {
      await notify(err.message || "Erro ao exportar relatorio.", "error");
    }
  };

  const handleExportXML = async () => {
    try {
      // Exemplo integrando com o endpoint SAFT/XML já existente
      const response = await api.post("/caixa/saft", {
        empresa_id: empresa?.id || user?.empresa_id,
        inicio: filtros.inicio,
        fim: filtros.fim
      });
      
      const xmlData = JSON.stringify(response.data, null, 2);
      const blob = new Blob([xmlData], { type: "application/xml" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fluxo_caixa_${filtros.inicio}_${filtros.fim}.xml`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      await notify("Ficheiro XML exportado com sucesso.", "success");
    } catch (err) {
      await notify("Erro ao exportar XML.", "error");
    }
  };

  const resumo = dadosCaixa?.resumo || { total_entradas: 0, total_saidas: 0, saldo_liquido: 0 };
  const entradas = dadosCaixa?.entradas || [];
  const saidas = dadosCaixa?.saidas || [];

  return (
    <ResourcePage 
      title="Fluxo de Caixa" 
      breadcrumb={["Gestão Financeira", "Fluxo de Caixa"]}
      actions={(
        <div className="d-flex align-items-center gap-2">
          <button 
            type="button" 
            className="btn btn-outline-danger btn-sm d-inline-flex align-items-center gap-1"
            onClick={() => downloadExport("pdf")}
          >
            <FileDown size={16} /> PDF
          </button>
          <button 
            type="button" 
            className="btn btn-outline-success btn-sm d-inline-flex align-items-center gap-1"
            onClick={() => downloadExport("excel")}
          >
            <FileSpreadsheet size={16} /> Excel
          </button>
          <button 
            type="button" 
            className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1"
            onClick={handleExportXML}
          >
            <FileCode size={16} /> XML
          </button>
        </div>
      )}
    >
      {/* Filtro de Período */}
      <div className="card bg-white border-0 shadow-sm mb-4">
        <div className="card-body py-3">
          <form onSubmit={handleFilterSubmit} className="row align-items-center g-3">
            <div className="col-md-auto">
              <span className="fw-semibold text-muted d-flex align-items-center gap-1">
                <Calendar size={18} /> Período:
              </span>
            </div>
            <div className="col-md-3">
              <input 
                type="date" 
                className="form-control form-control-sm" 
                name="inicio" 
                value={filtros.inicio} 
                onChange={handleDateChange} 
                required
              />
            </div>
            <div className="col-md-3">
              <input 
                type="date" 
                className="form-control form-control-sm" 
                name="fim" 
                value={filtros.fim} 
                onChange={handleDateChange} 
                required
              />
            </div>
            <div className="col-md-auto">
              <button 
                type="submit" 
                className="btn btn-primary btn-sm px-4" 
                disabled={loading}
              >
                {loading ? "A atualizar..." : "Filtrar Caixa"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Cartões de Resumo Financeiro */}
      <div className="row mb-4">
        <div className="col-xl-4 col-md-6 mb-4 mb-xl-0">
          <div className="card bg-white border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="text-success bg-success-subtle p-2 rounded d-inline-flex mb-3">
                <ArrowUpCircle size={22} />
              </div>
              <p className="text-muted mb-1 small">Total de Entradas (Receitas)</p>
              <h4 className="mb-0 text-success fw-bold">{Number(resumo.total_entradas).toLocaleString()} Kz</h4>
            </div>
          </div>
        </div>

        <div className="col-xl-4 col-md-6 mb-4 mb-xl-0">
          <div className="card bg-white border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="text-danger bg-danger-subtle p-2 rounded d-inline-flex mb-3">
                <ArrowDownCircle size={22} />
              </div>
              <p className="text-muted mb-1 small">Total de Saídas (Despesas)</p>
              <h4 className="mb-0 text-danger fw-bold">{Number(resumo.total_saidas).toLocaleString()} Kz</h4>
            </div>
          </div>
        </div>

        <div className="col-xl-4 col-md-12">
          <div className="card bg-white border-0 shadow-sm h-100">
            <div className="card-body">
              <div className={`p-2 rounded d-inline-flex mb-3 ${resumo.saldo_liquido >= 0 ? 'text-primary bg-primary-subtle' : 'text-warning bg-warning-subtle'}`}>
                <DollarSign size={22} />
              </div>
              <p className="text-muted mb-1 small">Saldo Líquido do Período</p>
              <h4 className={`mb-0 fw-bold ${resumo.saldo_liquido >= 0 ? 'text-primary' : 'text-danger'}`}>
                {Number(resumo.saldo_liquido).toLocaleString()} Kz
              </h4>
            </div>
          </div>
        </div>
      </div>

      {/* Tabelas de Movimentos (Entradas e Saídas) */}
      <div className="row">
        {/* Tabela de Entradas */}
        <div className="col-lg-6 mb-4">
          <div className="card bg-white border-0 shadow-sm h-100">
            <div className="card-body">
              <h4 className="card-title mb-3 fs-5 d-flex align-items-center gap-2 text-success">
                <ArrowUpCircle size={18} /> Entradas Registadas
              </h4>
              <div className="table-responsive">
                <table className="table table-centered table-hover mb-0 align-middle">
                  <thead>
                    <tr className="text-muted fs-7">
                      <th>Tipo / Nº</th>
                      <th>Data</th>
                      <th className="text-end">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entradas.length > 0 ? (
                      entradas.map((item) => (
                        <tr key={item.id}>
                          <td><span className="fw-semibold">{item.tipo}</span> {item.numero}</td>
                          <td>{item.data}</td>
                          <td className="text-end fw-bold text-success">{Number(item.valor).toLocaleString()} Kz</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center text-muted py-3">Nenhuma entrada registada no período.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Tabela de Saídas */}
        <div className="col-lg-6 mb-4">
          <div className="card bg-white border-0 shadow-sm h-100">
            <div className="card-body">
              <h4 className="card-title mb-3 fs-5 d-flex align-items-center gap-2 text-danger">
                <ArrowDownCircle size={18} /> Saídas Registadas (Despesas)
              </h4>
              <div className="table-responsive">
                <table className="table table-centered table-hover mb-0 align-middle">
                  <thead>
                    <tr className="text-muted fs-7">
                      <th>Descrição</th>
                      <th>Data</th>
                      <th className="text-end">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {saidas.length > 0 ? (
                      saidas.map((item) => (
                        <tr key={item.id}>
                          <td>{item.descricao}</td>
                          <td>{item.data}</td>
                          <td className="text-end fw-bold text-danger">{Number(item.valor).toLocaleString()} Kz</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center text-muted py-3">Nenhuma saída registada no período.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ResourcePage>
  );
};

export default FluxoCaixa;
